import React, { useEffect, useMemo, useRef, useState } from 'react';
import Navbar from '../components/Navbar';
import { API_BASE_URL, API_ENDPOINTS } from '../constants/apiConstants';
import { getStoredTokens, getStoredUserId } from '../utils/auth';
import './CSS/UGCVideos.css';

const UGCVideos = () => {
    const [prompt, setPrompt] = useState('');
    const [productImage, setProductImage] = useState(null);
    const [videos, setVideos] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [generationNotice, setGenerationNotice] = useState('');
    const [error, setError] = useState('');
    const [latestVideoUrl, setLatestVideoUrl] = useState('');
    const [latestVideoS3Url, setLatestVideoS3Url] = useState('');
    const pollingRef = useRef(null);
    const pendingRef = useRef({ requestedAt: null, initialCount: 0, deadline: 0 });

    const tokens = getStoredTokens();
    const storedUserId = getStoredUserId();

    const buildBffUrl = (path) => {
        const normalizedPath = path.startsWith('/') ? path : `/${path}`;
        const baseUrl = API_BASE_URL?.replace(/\/$/, '') || '';
        if (baseUrl.endsWith('/api/bff') && normalizedPath.startsWith('/api/bff/')) {
            return `${baseUrl}${normalizedPath.replace('/api/bff', '')}`;
        }
        return `${baseUrl}${normalizedPath}`;
    };

    const sortedVideos = useMemo(() => {
        return [...videos].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }, [videos]);

    const fetchVideos = async ({ silent = false } = {}) => {
        if (!tokens?.idToken) return;
        if (!silent) {
            setIsLoading(true);
            setError('');
        }

        try {
            const response = await fetch(buildBffUrl(API_ENDPOINTS.UGC_LIST), {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${tokens.idToken}`,
                    userid: storedUserId || '',
                },
            });

            const data = await response.json();
            if (!response.ok || !data.success) {
                throw new Error(data.message || 'Failed to load UGC videos');
            }

            const nextVideos = Array.isArray(data.data) ? data.data : [];
            setVideos(nextVideos);
            return nextVideos;
        } catch (err) {
            console.error('Failed to load UGC videos', err);
            if (!silent) {
                setError(err.message || 'Failed to load UGC videos.');
            }
        } finally {
            if (!silent) {
                setIsLoading(false);
            }
        }
        return null;
    };

    useEffect(() => {
        fetchVideos();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        return () => {
            if (latestVideoUrl) {
                URL.revokeObjectURL(latestVideoUrl);
            }
        };
    }, [latestVideoUrl]);

    useEffect(() => {
        return () => {
            if (pollingRef.current) {
                clearInterval(pollingRef.current);
            }
        };
    }, []);

    const stopPolling = () => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
    };

    const startPolling = (requestedAt, initialCount) => {
        stopPolling();
        pendingRef.current = {
            requestedAt,
            initialCount,
            deadline: Date.now() + 10 * 60 * 1000,
        };

        pollingRef.current = setInterval(async () => {
            const { requestedAt: requestedTs, initialCount: count, deadline } = pendingRef.current;
            if (!requestedTs || Date.now() > deadline) {
                stopPolling();
                setGenerationNotice('Still rendering. Check your UGC library in a few minutes.');
                return;
            }

            const list = await fetchVideos({ silent: true });
            if (!Array.isArray(list)) return;

            const newest = list.find((video) => {
                if (!video?.created_at) return false;
                return new Date(video.created_at).getTime() >= requestedTs;
            });

            if (list.length > count && newest) {
                setGenerationNotice('Your UGC video is ready.');
                setLatestVideoS3Url(newest.s3_url || '');
                stopPolling();
            }
        }, 15000);
    };

    const handleFileChange = (event) => {
        const file = event.target.files?.[0];
        setProductImage(file || null);
    };

    const handleGenerate = async () => {
        if (!tokens?.idToken) {
            setError('You need to be logged in to generate a UGC video.');
            return;
        }

        if (!prompt.trim()) {
            setError('Please enter a base prompt.');
            return;
        }

        if (!productImage) {
            setError('Please upload a product image.');
            return;
        }

        setIsGenerating(true);
        setError('');
        setGenerationNotice('');

        try {
            const formData = new FormData();
            formData.append('prompt', prompt.trim());
            formData.append('product_image', productImage);

            const response = await fetch(buildBffUrl(API_ENDPOINTS.UGC_GENERATE), {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${tokens.idToken}`,
                    userid: storedUserId || '',
                },
                body: formData,
            });

            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(data.message || 'UGC video generation failed');
            }

            setLatestVideoUrl('');
            setGenerationNotice(data.message || 'UGC video generation started. We will update this page once it is ready.');
            startPolling(Date.now(), videos.length);
        } catch (err) {
            console.error('UGC video generation failed', err);
            setError(err.message || 'UGC video generation failed.');
        } finally {
            setIsGenerating(false);
        }
    };

    const latestPreviewUrl = latestVideoUrl || latestVideoS3Url;

    return (
        <div className="ugc-shell">
            <Navbar />
            <div className="ugc-container">
                <section className="ugc-hero">
                    <div>
                        <p className="ugc-eyebrow">UGC VIDEO STUDIO</p>
                        <h1>Create a high-impact 8s UGC ad</h1>
                        <p className="ugc-subtitle">
                            Upload your product shot and drop a base prompt. We refine the script, generate the caption,
                            and deliver an 8-second video ready to use.
                        </p>
                    </div>
                    <div className="ugc-hero-card">
                        <h3>What you get</h3>
                        <ul>
                            <li>AI-written script + caption</li>
                            <li>Veo 3.1 rendered 8s video</li>
                            <li>Saved to your UGC library</li>
                        </ul>
                    </div>
                </section>

                <section className="ugc-panel">
                    <div className="ugc-panel-header">
                        <h2>Generate UGC video</h2>
                        <span className="ugc-pill">8 seconds • 16:9 • 720p</span>
                    </div>

                    <div className="ugc-form">
                        <label className="ugc-field">
                            Base prompt
                            <textarea
                                value={prompt}
                                onChange={(event) => setPrompt(event.target.value)}
                                placeholder="Example: Make a modern, minimal ad showcasing the product's premium quality."
                                rows={4}
                            />
                        </label>

                        <label className="ugc-field">
                            Product image
                            <input type="file" accept="image/*" onChange={handleFileChange} />
                            {productImage && <span className="ugc-file">{productImage.name}</span>}
                        </label>

                        {error && <p className="ugc-error">{error}</p>}
                        {generationNotice && <p className="ugc-info">{generationNotice}</p>}

                        <button className="ugc-button" onClick={handleGenerate} disabled={isGenerating}>
                            {isGenerating ? 'Generating video...' : 'Generate UGC video'}
                        </button>
                    </div>
                </section>

                <section className="ugc-panel">
                    <div className="ugc-panel-header">
                        <h2>Your latest render</h2>
                        {latestVideoS3Url && (
                            <a className="ugc-link" href={latestVideoS3Url} target="_blank" rel="noreferrer">
                                Open S3 link
                            </a>
                        )}
                    </div>
                    {latestPreviewUrl ? (
                        <video className="ugc-video" controls src={latestPreviewUrl} />
                    ) : (
                        <p className="ugc-muted">No video yet. Generate one to preview here.</p>
                    )}
                </section>

                <section className="ugc-panel">
                    <div className="ugc-panel-header">
                        <h2>UGC library</h2>
                        {isLoading && <span className="ugc-muted">Loading...</span>}
                    </div>

                    {!isLoading && sortedVideos.length === 0 && (
                        <p className="ugc-muted">No saved UGC videos yet.</p>
                    )}

                    <div className="ugc-grid">
                        {sortedVideos.map((video) => (
                            <div className="ugc-card" key={video.id || video.s3_key}>
                                <video className="ugc-card-video" controls src={video.s3_url} />
                                <div className="ugc-card-body">
                                    <h4>{video.prompt || 'UGC video'}</h4>
                                    <p>{video.caption || 'Caption coming soon.'}</p>
                                    <span className="ugc-timestamp">
                                        {video.created_at ? new Date(video.created_at).toLocaleString() : ''}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            </div>
        </div>
    );
};

export default UGCVideos;
